import React from 'react';
import { AlertTriangle, Calendar, DollarSign, User, FileText, X } from 'lucide-react';
import { format } from 'date-fns';
import { StyledModal, StyledModalSection, StyledModalButton } from '@/components/ui/styled-modal';
import { DuplicateCheckResult, PotentialDuplicate } from '@flows.cash/core';
import { Transaction } from '@/types/transaction';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';

interface DuplicateConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (forceAdd: boolean) => void;
  duplicateResult: DuplicateCheckResult;
  newTransaction: Partial<Transaction>;
}

export function DuplicateConfirmation({
  isOpen,
  onClose,
  onConfirm,
  duplicateResult,
  newTransaction
}: DuplicateConfirmationProps) {
  const { accounts } = useAccountsStore();
  const { categories } = useCategoriesStore();
  const { payees } = usePayeesStore();

  const getAccountName = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.name || 'Unknown Account';
  };

  const getCategoryName = (categoryId: string | null | undefined) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Unknown Category';
  };

  const getPayeeName = (payeeId: string | null | undefined) => {
    if (!payeeId) return 'No Payee';
    const payee = payees.find(p => p.id === payeeId);
    return payee?.name || 'Unknown Payee';
  };

  const formatAmount = (amount: number, currency: string = 'USD') => {
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(absAmount);
    
    return isNegative ? `-${formatted}` : formatted;
  };

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, 'MMM d, yyyy HH:mm');
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-red-600';
    if (confidence >= 0.7) return 'text-orange-600';
    return 'text-yellow-600';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.9) return 'Very High';
    if (confidence >= 0.7) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  const TransactionCard = ({ 
    transaction, 
    title, 
    isNew = false 
  }: { 
    transaction: Partial<Transaction>, 
    title: string,
    isNew?: boolean 
  }) => (
    <div className={`p-4 rounded-xl border-2 ${
      isNew 
        ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30' 
        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/30'
    }`}>
      <h4 className={`font-semibold mb-3 ${
        isNew ? 'text-blue-800 dark:text-blue-200' : 'text-gray-800 dark:text-gray-200'
      }`}>
        {title}
      </h4>
      
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <DollarSign className="w-4 h-4 text-gray-500" />
          <span className="font-medium">
            {formatAmount(transaction.amount || 0, transaction.currency)}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs ${
            (transaction.amount || 0) >= 0 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
          }`}>
            {transaction.type || 'expense'}
          </span>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(transaction.date || new Date())}</span>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <FileText className="w-4 h-4" />
          <span>{getAccountName(transaction.account_id || '')}</span>
        </div>
        
        {transaction.description && (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Description:</strong> {transaction.description}
          </div>
        )}
        
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Category:</strong> {getCategoryName(transaction.category_id)}
        </div>
        
        {(transaction.payee_id || transaction.payee_name) && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <User className="w-4 h-4" />
            <span>{transaction.payee_name || getPayeeName(transaction.payee_id)}</span>
          </div>
        )}
      </div>
    </div>
  );

  const DuplicateCard = ({ duplicate }: { duplicate: PotentialDuplicate }) => (
    <div className="border border-orange-200 rounded-xl p-4 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-orange-800 dark:text-orange-200">
          Potential Duplicate
        </h4>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${getConfidenceColor(duplicate.confidence)}`}>
            {getConfidenceLabel(duplicate.confidence)} Confidence
          </span>
          <span className="text-xs text-gray-500">
            ({Math.round(duplicate.confidence * 100)}%)
          </span>
        </div>
      </div>
      
      <TransactionCard transaction={duplicate.existingTransaction} title="" />
      
      <div className="mt-3">
        <h5 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
          Match Reasons:
        </h5>
        <div className="flex flex-wrap gap-1">
          {duplicate.matchReasons.map((reason: string, index: number) => (
            <span 
              key={index}
              className="px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded-full dark:bg-orange-800 dark:text-orange-200"
            >
              {reason}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <StyledModal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Potential Duplicate Transaction"
      maxWidth="1024px"
    >
      <div className="space-y-6">
        {/* Warning Header */}
        <div className="flex items-center gap-3 p-4 bg-orange-100 dark:bg-orange-900/30 rounded-xl border border-orange-200 dark:border-orange-800">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
          <div>
            <h3 className="font-semibold text-orange-800 dark:text-orange-200">
              Duplicate Transaction Detected
            </h3>
            <p className="text-sm text-orange-700 dark:text-orange-300">
              We found {duplicateResult.potentialDuplicates.length} potentially similar transaction{duplicateResult.potentialDuplicates.length > 1 ? 's' : ''} in your records.
            </p>
          </div>
        </div>

        {/* New Transaction */}
        <StyledModalSection title="New Transaction" primaryColor="#f97316">
          <TransactionCard 
            transaction={newTransaction} 
            title="Transaction you're adding"
            isNew={true}
          />
        </StyledModalSection>

        {/* Potential Duplicates */}
        <StyledModalSection 
          title={`Potential Duplicate${duplicateResult.potentialDuplicates.length > 1 ? 's' : ''} Found`}
          primaryColor="#f97316"
        >
          <div className="space-y-4">
            {duplicateResult.potentialDuplicates.map((duplicate: PotentialDuplicate, index: number) => (
              <DuplicateCard key={index} duplicate={duplicate} />
            ))}
          </div>
        </StyledModalSection>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <StyledModalButton
            onClick={() => onConfirm(false)}
            variant="secondary"
            className="flex-1"
          >
            Cancel Addition
          </StyledModalButton>
          
          <StyledModalButton
            onClick={() => onConfirm(true)}
            variant="primary"
            className="flex-1"
          >
            Add Anyway
          </StyledModalButton>
        </div>
        
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Review the transactions above to determine if this is truly a duplicate. 
          You can adjust duplicate detection settings in your preferences.
        </div>
      </div>
    </StyledModal>
  );
} 